/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { History } from 'history';
import { OpPatch } from 'json-patch';
import { ActionType, createAction, createAsyncAction } from 'typesafe-actions';
import { FollowUpsMap } from './reducers';
import { AjaxError } from 'rxjs/ajax';

export interface CommonFollowUpData {
  id: string;
  resolved?: string;
  farm: string;
  facility: string;
  evaluator: string;
  evaluation: string;
  title: string;
  issue: string;
  resolution: string;
  related_questions?: string[];
  comments?: string;
  history: Required<FollowUpVisitData>[];
  created: string;
  // server-side modified
  updated: string;
}

export interface StandardFollowUpData extends CommonFollowUpData {
  due: string;
}

export interface ImmediateFollowUpData extends CommonFollowUpData {
  due: undefined;
  immediate_follow_up_date: string;
  immediate_resolved?: string;
  week_follow_up_date: string;
  week_resolved?: string;
  month_follow_up_date: string;
  month_resolved?: string;
  month_three_follow_up_date: string;
  month_three_resolved?: string;
}

export type FollowUpData = StandardFollowUpData | ImmediateFollowUpData;

interface CommonFollowUpEditorData {
  modified?: string;
  syncStatus?: 'syncd' | 'dirty' | 'uploading';
}
interface StandardFollowUpEditorData extends CommonFollowUpEditorData {
  actionPlan: StandardFollowUpData;
  visit: Partial<FollowUpVisitData>;
}

interface ImmediateFollowUpEditorData extends CommonFollowUpEditorData {
  actionPlan: ImmediateFollowUpData;
  visit: Partial<FollowUpVisitData>;
}

export type FollowUpVisitData = {
  visit_date?: string;
  action?: 'Resolve' | 'Fail' | 'Update Notes';
  comments?: string;
};

export type RemoteFollowUpData = Pick<CommonFollowUpData, 'id' | 'facility'>;

export interface StandardFollowUp {
  type: 'mcap' | 'cip';
  data: StandardFollowUpData;
}
export interface ImmediateFollowUp {
  type: 'iap';
  data: ImmediateFollowUpData;
}

export type FollowUp = StandardFollowUp | ImmediateFollowUp;

export const loadFollowUpFacilities = createAction(
  'LOAD_FU_FACILITIES',
  (action) => (limit: number = 25, offset: number = 0) => action({ limit, offset })
);

export const syncFollowUps = createAsyncAction(
  'SYNC_FOLLOW_UPS_REQUEST',
  'SYNC_FOLLOW_UPS_SUCCESS',
  'SYNC_FOLLOW_UPS_FAILURE'
)<undefined, { [facilityId: string]: FollowUp[] }, Error>();

export const setFollowUps = createAction('SET_FOLLOW_UPS', (action) => (followUps: FollowUpsMap) => action(followUps));

export const submitFollowUp = createAsyncAction(
  'SUBMIT_FOLLOW_UP_REQUEST',
  'SUBMIT_FOLLOW_UP_SUCCESS',
  'SUBMIT_FOLLOW_UP_FAILURE'
)<{
  type: string,
  id: string,
}, FollowUpData, AjaxError>();

export const openFollowUpEditor = createAction(
  'OPEN_FOLLOW_UP_EDITOR',
  (action) => (facilityId: string, id: string, history: History<any>) => action({ facilityId, id, history })
);

export const updateFollowUp = createAction('UPDATE_FOLLOW_UP', (action) => (id: string, op: OpPatch) => action(op, id));

export const removeFollowUp = createAction('REMOVE_FOLLOW_UP_REQUEST', (action) => (id: string) => action(id));

export const clearAllFollowUps = createAction('CLEAR_ALL_FOLLOW_UPS');
export const throttledSyncFollowUps = createAction('THROTTLED_SYNC_FOLLOWUPS');
// export const getFollowUpsForFacility = createAction('GET_FOLLOWUPS_FOR_FACILITY', (action) => (id: string) => action(id));
export const getFollowUpsForFacility = createAsyncAction(
  'GET_FOLLOWUPS_FOR_FACILITY_REQUEST',
  'GET_FOLLOWUPS_FOR_FACILITY_SUCCESS',
  'GET_FOLLOWUPS_FOR_FACILITY_FAILURE'
)<string, {id: string, followUps: FollowUp[]}, AjaxError>();

export type FollowUpActions =
  | ActionType<typeof loadFollowUpFacilities>
  | ActionType<typeof syncFollowUps>
  | ActionType<typeof setFollowUps>
  | ActionType<typeof openFollowUpEditor>
  | ActionType<typeof submitFollowUp>
  | ActionType<typeof updateFollowUp>
  | ActionType<typeof removeFollowUp>
  | ActionType<typeof clearAllFollowUps>
  | ActionType<typeof throttledSyncFollowUps>
  | ActionType<typeof getFollowUpsForFacility>;
